import { TableActionButton, TableActionGroup } from "@channelplay/table-actions";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Loader2, AlertTriangle } from 'lucide-react';
import { salesTargetsAPI } from '../../services/api';
import Modal from '../../components/Modal';
import { SearchInput } from '../../components/SearchInput';
import { PageErrorBanner } from '../../components/PageErrorBanner';
import { BlockLoader } from '../../components/loading';
import { FormErrorBanner } from '../../components/FormErrorBanner';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';
import {
  MATCH_OP_LABELS, VALUELESS_OPS, NUMERIC_OPS,
  type MultiplierRule, type MultiplierField, type MultiplierPreviewRow,
  type SalesTargetConfig, type Scope, type AppliesTo,
} from './types';

/**
 * How a store target becomes a promoter target.
 *
 * ── THE RULE THE WHOLE MODULE TURNS ON ───────────────────────────────────────
 * Every promoter deployed at a store carries `store target x multiplier`. A
 * multiplier of 1 hands the entire store target to one person, which is only
 * coherent when exactly one person is deployed there. Two promoters at 1 each
 * means the store is asked for twice what it was given — so the calculation
 * refuses it, and the Results tab says which stores and by how much.
 *
 * ── WHY THE PREVIEW IS NOT OPTIONAL ──────────────────────────────────────────
 * Rules COMPOSE across fields: Tier A (0.5) in an LFR trade (0.9) gives 0.45.
 * That number is not readable off the rule list, and an admin who cannot see the
 * result will not trust it. The preview also names the rules that never fire,
 * which is the usual cause of "why is this store still at 1".
 */

interface Props {
  projectId: number;
  config: SalesTargetConfig;
}

const APPLIES_TO_LABEL: Record<AppliesTo, string> = {
  both: 'Targets and sales',
  target: 'Targets only',
  sales: 'Sales only',
};

const emptyDraft = (scopeType: Scope) => ({
  id: null as string | null,
  scopeType,
  fieldSource: '' as 'column' | 'custom_field' | '',
  fieldKey: '',
  matchOp: 'eq',
  matchValue: '' as unknown,
  multiplier: '0.5',
  appliesTo: 'both' as AppliesTo,
});

export default function MultipliersTab({ projectId, config }: Props) {
  const [scope, setScope] = useState<Scope>(config.sourceScope);
  const [rules, setRules] = useState<MultiplierRule[]>([]);
  const [fields, setFields] = useState<MultiplierField[]>([]);
  const [preview, setPreview] = useState<MultiplierPreviewRow[]>([]);
  const [neverFires, setNeverFires] = useState<string[]>([]);
  const [showPreview, setShowPreview] = useState(true);
  const [search, setSearch] = useState('');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<ReturnType<typeof emptyDraft> | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<MultiplierRule | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ruleRes, fieldRes, previewRes] = await Promise.all([
        salesTargetsAPI.listMultipliers(projectId, scope, true),
        salesTargetsAPI.listMultiplierFields(projectId, scope),
        salesTargetsAPI.previewMultipliers(projectId, scope),
      ]);
      setRules(ruleRes.data.data ?? []);
      setFields(fieldRes.data.data ?? []);
      setPreview(previewRes.data.data.rows ?? []);
      setNeverFires(previewRes.data.data.neverFires ?? []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not load the multipliers.');
    } finally {
      setLoading(false);
    }
  }, [projectId, scope]);

  useEffect(() => { void load(); }, [load]);

  const fieldByKey = useMemo(
    () => new Map(fields.map((f) => [`${f.source}::${f.key}`, f])),
    [fields],
  );

  const selectedField = draft ? fieldByKey.get(`${draft.fieldSource}::${draft.fieldKey}`) : undefined;
  const isNumericField = selectedField?.kind === 'number';
  const availableOps = useMemo(() => {
    const base = ['eq', 'neq', 'in', 'not_in', 'blank', 'not_blank'];
    return isNumericField ? [...NUMERIC_OPS, ...base] : base;
  }, [isNumericField]);

  const filteredPreview = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return preview;
    return preview.filter((r) =>
      (r.code ?? '').toLowerCase().includes(q) || (r.name ?? '').toLowerCase().includes(q));
  }, [preview, search]);

  // The number an admin actually wants: how many stores are still at 1, because
  // those are the ones that will be refused the moment a second promoter lands.
  const atOne = useMemo(() => preview.filter((r) => r.multiplier === 1).length, [preview]);

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setFormError(null);
    try {
      const value = VALUELESS_OPS.has(draft.matchOp) ? null
        : draft.matchOp === 'between'
          ? String(draft.matchValue ?? '').split(',').map((v) => v.trim()).filter(Boolean)
          : draft.matchOp === 'in' || draft.matchOp === 'not_in'
            ? String(draft.matchValue ?? '').split(',').map((v) => v.trim()).filter(Boolean)
            : draft.matchValue;
      const body = {
        scopeType: draft.scopeType,
        fieldSource: draft.fieldSource,
        fieldKey: draft.fieldKey,
        matchOp: draft.matchOp,
        matchValue: value,
        multiplier: Number(draft.multiplier),
        appliesTo: draft.appliesTo,
      };
      if (draft.id) await salesTargetsAPI.updateMultiplier(projectId, draft.id, body);
      else await salesTargetsAPI.createMultiplier(projectId, body);
      setDraft(null);
      await load();
    } catch (err: any) {
      setFormError(err.response?.data?.message || 'Could not save the multiplier.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!deleting) return;
    try {
      await salesTargetsAPI.deleteMultiplier(projectId, deleting.id);
      setDeleting(null);
      await load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not delete the multiplier.');
    }
  };

  const toggleActive = async (rule: MultiplierRule) => {
    try {
      await salesTargetsAPI.setMultiplierActive(projectId, rule.id, !rule.active);
      await load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not change the multiplier.');
    }
  };

  const describeValue = (rule: MultiplierRule): string => {
    if (VALUELESS_OPS.has(rule.matchOp)) return '';
    if (Array.isArray(rule.matchValue)) return rule.matchValue.join(rule.matchOp === 'between' ? ' and ' : ', ');
    return String(rule.matchValue ?? '');
  };

  if (loading) {
    return (
      <BlockLoader className="rounded-xl border border-gray-200 bg-white py-20" />
    );
  }

  return (
    <div className="space-y-4">
      {error && <PageErrorBanner message={error} />}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-600">Applies to</span>
          <div className="flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
            {(['store', 'employee'] as Scope[]).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setScope(s)}
                className={`rounded-md px-3 py-1 text-sm font-medium ${
                  scope === s ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-600'
                }`}
              >
                {s === 'store' ? 'Store fields' : 'Employee fields'}
              </button>
            ))}
          </div>
        </div>
        <button
          type="button"
          onClick={() => { setDraft(emptyDraft(scope)); setFormError(null); }}
          className="flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-2 text-sm font-semibold text-white hover:bg-primary-700"
        >
          <Plus className="h-4 w-4" />
          Add a multiplier
        </button>
      </div>

      {rules.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white px-6 py-12 text-center">
          <h3 className="text-base font-medium text-gray-700">No multipliers yet</h3>
          <p className="mx-auto mt-2 max-w-xl text-sm text-gray-500">
            Without one, every promoter at a store carries the whole store target. That is correct
            for a store with one promoter and wrong for a store with two — the calculation will
            refuse those and list them on the Results tab. Add a rule keyed on whatever tells those
            stores apart: {fields.slice(0, 4).map((f) => f.label).join(', ') || 'a store field'}.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <th className="px-4 py-3">When</th>
                <th className="px-4 py-3 text-right">Multiplier</th>
                <th className="px-4 py-3">Used for</th>
                <th className="px-4 py-3">Status</th>
                <th className=" px-4 py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rules.map((rule) => {
                const dead = neverFires.includes(rule.id) && rule.active;
                return (
                  <tr key={rule.id} className={rule.active ? '' : 'bg-gray-50/60'}>
                    <td className="px-4 py-3">
                      <span className="font-medium text-gray-900">{rule.fieldLabel ?? rule.fieldKey}</span>
                      <span className="text-gray-600"> {MATCH_OP_LABELS[rule.matchOp] ?? rule.matchOp} </span>
                      <span className="font-medium text-gray-900">{describeValue(rule)}</span>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900">
                      {rule.multiplier}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{APPLIES_TO_LABEL[rule.appliesTo]}</td>
                    <td className="px-4 py-3">
                      {!rule.active ? (
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">Off</span>
                      ) : dead ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800">
                          <AlertTriangle className="h-3 w-3" />
                          Never matches
                        </span>
                      ) : (
                        <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-800">On</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <TableActionGroup>
                        <TableActionButton action={rule.active ? 'deactivate' : 'activate'} label={rule.active ? 'Switch off' : 'Switch on'}
                          type="button"
                          title={rule.active ? 'Switch off' : 'Switch on'}
                          onClick={() => toggleActive(rule)} />
                        <TableActionButton action="edit" label="Edit"
                          type="button"
                          title="Edit"
                          onClick={() => {
                            setDraft({
                              id: rule.id,
                              scopeType: rule.scopeType,
                              fieldSource: rule.fieldSource,
                              fieldKey: rule.fieldKey,
                              matchOp: rule.matchOp,
                              matchValue: Array.isArray(rule.matchValue)
                                ? rule.matchValue.join(', ') : rule.matchValue,
                              multiplier: String(rule.multiplier),
                              appliesTo: rule.appliesTo,
                            });
                            setFormError(null);
                          }} />
                        <TableActionButton action="delete" label="Delete"
                          type="button"
                          title="Delete"
                          onClick={() => setDeleting(rule)} />
                      </TableActionGroup>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* The preview. Rules compose, so this is the only place the real figure
          is visible — without it the rule list is a guess. */}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              What every {scope === 'store' ? 'store' : 'promoter'} ends up with
            </h3>
            <p className="mt-0.5 text-xs text-gray-500">
              Rules for different fields multiply together. Nothing matching leaves it at 1.
              {atOne > 0 && (
                <>
                  {' '}
                  <span className="font-medium text-amber-700">
                    {atOne} still at 1
                  </span>
                  {scope === 'store' && ' — fine where one promoter is deployed, refused where two are.'}
                </>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {showPreview && (
              <div className="w-56">
                <SearchInput value={search} onChange={setSearch} placeholder="Search" />
              </div>
            )}
            <button
              type="button"
              onClick={() => setShowPreview((v) => !v)}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              {showPreview ? 'Hide' : 'Show'}
            </button>
          </div>
        </div>

        {showPreview && (
          <div className="max-h-96 overflow-y-auto">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 bg-gray-50">
                <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <th className="px-4 py-2">{scope === 'store' ? 'Store' : 'Promoter'}</th>
                  <th className="px-4 py-2">Name</th>
                  <th className="px-4 py-2">{scope === 'store' ? 'Category / Trade' : 'Designation'}</th>
                  <th className="px-4 py-2 text-right">Multiplier</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredPreview.slice(0, 300).map((r) => (
                  <tr key={r.key} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-medium text-gray-900">{r.code}</td>
                    <td className="px-4 py-2 text-gray-600">{r.name}</td>
                    <td className="px-4 py-2 text-gray-500">
                      {[r.category, r.trade, r.city].filter(Boolean).join(' · ') || '—'}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <span className={r.multiplier === 1 ? 'font-semibold text-amber-700' : 'font-semibold text-gray-900'}>
                        {r.multiplier}
                      </span>
                      {r.applied.length > 1 && (
                        <span className="ml-2 text-xs text-gray-400">
                          {r.applied.map((a) => a.multiplier).join(' × ')}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
                {filteredPreview.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-10 text-center text-sm text-gray-500">
                      Nothing matches that search.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {filteredPreview.length > 300 && (
              <p className="px-4 py-2 text-xs text-gray-500">
                Showing the first 300 of {filteredPreview.length}. Search to narrow it.
              </p>
            )}
          </div>
        )}
      </div>

      <Modal
        isOpen={draft !== null}
        onClose={() => setDraft(null)}
        title={draft?.id ? 'Edit multiplier' : 'Add a multiplier'}
        size="lg"
        footer={
          <div className="flex w-full justify-end gap-2">
            <button
              type="button"
              onClick={() => setDraft(null)}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving || !draft?.fieldKey}
              className="flex items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Save
            </button>
          </div>
        }
      >
        {draft && (
          <div className="space-y-4">
            {formError && <FormErrorBanner message={formError} />}

            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600" htmlFor="mult-field">
                When this field
              </label>
              <select
                id="mult-field"
                value={`${draft.fieldSource}::${draft.fieldKey}`}
                onChange={(e) => {
                  const [source, ...rest] = e.target.value.split('::');
                  setDraft({
                    ...draft,
                    fieldSource: source as 'column' | 'custom_field',
                    fieldKey: rest.join('::'),
                    matchValue: '',
                  });
                }}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
              >
                <option value="::">Choose a field…</option>
                {fields.map((f) => (
                  <option key={`${f.source}::${f.key}`} value={`${f.source}::${f.key}`}>
                    {f.label}{f.source === 'custom_field' ? ' (custom field)' : ''}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                The list is this project&apos;s own {scope} fields, including any custom fields it
                has defined.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600" htmlFor="mult-op">
                  Is
                </label>
                <select
                  id="mult-op"
                  value={draft.matchOp}
                  onChange={(e) => setDraft({ ...draft, matchOp: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                >
                  {availableOps.map((op) => (
                    <option key={op} value={op}>{MATCH_OP_LABELS[op]}</option>
                  ))}
                </select>
              </div>
              {!VALUELESS_OPS.has(draft.matchOp) && (
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600" htmlFor="mult-value">
                    This value
                  </label>
                  {selectedField?.options && (draft.matchOp === 'eq' || draft.matchOp === 'neq') ? (
                    <select
                      id="mult-value"
                      value={String(draft.matchValue ?? '')}
                      onChange={(e) => setDraft({ ...draft, matchValue: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                    >
                      <option value="">Choose…</option>
                      {selectedField.options.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input
                      id="mult-value"
                      type="text"
                      value={String(draft.matchValue ?? '')}
                      onChange={(e) => setDraft({ ...draft, matchValue: e.target.value })}
                      placeholder={
                        draft.matchOp === 'between' ? 'lower, upper'
                          : draft.matchOp === 'in' || draft.matchOp === 'not_in' ? 'one, two, three'
                            : ''
                      }
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                    />
                  )}
                  {(draft.matchOp === 'in' || draft.matchOp === 'not_in' || draft.matchOp === 'between') && (
                    <p className="mt-1 text-xs text-gray-500">Separate values with a comma.</p>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600" htmlFor="mult-value-n">
                  Then the multiplier is
                </label>
                <input
                  id="mult-value-n"
                  type="number"
                  min={0}
                  max={1}
                  step="0.01"
                  value={draft.multiplier}
                  onChange={(e) => setDraft({ ...draft, multiplier: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Between 0 and 1. Use 1 only where a single promoter carries the whole store; 0.5
                  where two share it.
                </p>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600" htmlFor="mult-applies">
                  Used for
                </label>
                <select
                  id="mult-applies"
                  value={draft.appliesTo}
                  onChange={(e) => setDraft({ ...draft, appliesTo: e.target.value as AppliesTo })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                >
                  <option value="both">Targets and sales</option>
                  <option value="target">Targets only</option>
                  <option value="sales">Sales only</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Leave this on both unless you have a reason not to — achievement is sales divided
                  by target, and splitting them makes that percentage meaningless.
                </p>
              </div>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDeleteModal
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={remove}
        isSubmitting={false}
        title="Delete this multiplier?"
        entityLabel="multiplier"
        entityName={deleting ? `${deleting.fieldLabel ?? deleting.fieldKey} → ${deleting.multiplier}` : ''}
        consequenceNote={
          'Every target derived from it is recalculated the next time anyone looks, including for '
          + 'months already closed. Switch it off instead if you only want it to stop applying.'
        }
      />
    </div>
  );
}
